import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {Enzyme} from 'molbio/enzymes/EnzymeConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, RED} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

export interface EnzymeTableRow {
  enzymeId: number;
  enzyme: string;
  cuts?: number;
}

interface CodonOptimizationCutsitesPickerProps {
  rows: Array<EnzymeTableRow>;
  onAdd: (enzymeId: number) => void;
  onDelete: (enzymeId: number) => void;
  onDeleteAll: () => void;
  isShowingCuts: boolean;
  buttonText: string;
  emptyTableText: string;
  availableEnzymes: Array<Enzyme>;
  dataTestAttrs?: DataTestAttrs;
}

export default function CodonOptimizationCutsitesPicker(
  props: CodonOptimizationCutsitesPickerProps
): JSX.Element {
  if (props.isShowingCuts && props.rows.length > 0) {
    assert(props.rows[0].hasOwnProperty('cuts'), 'Expected rows to contain a value for cuts.');
  }
  const rowIds = props.rows.map((row) => row.enzymeId);
  return (
    <div
      {...getTestProps(
        props.dataTestAttrs ?? {component: 'CodonOptimizationCutsitesPicker', element: 'root'}
      )}
    >
      <div>
        <Table numRows={props.rows.length} emptyTableText={props.emptyTableText}>
          <Column header="Enzyme Name" renderCell={(rowIndex: number) => props.rows[rowIndex].enzyme} />
          {props.isShowingCuts && (
            <Column header="Cuts" renderCell={(rowIndex: number) => props.rows[rowIndex].cuts} />
          )}
          <Column
            header=""
            renderCell={(rowIndex: number) => (
              <Cell>
                <BenchlingIcon
                  onClick={() => {
                    props.onDelete(props.rows[rowIndex].enzymeId);
                  }}
                  icon="delete"
                  className={css(styles.deleteButton)}
                  {...getTestProps({
                    component: 'CodonOptimizationCutsitesPicker',
                    element: 'deleteCutsiteButton',
                    index: String(rowIndex),
                  })}
                  data-amp-safe={false}
                />
              </Cell>
            )}
          />
        </Table>
      </div>
      <Dropdown
        closeOnInsideClick={true}
        closeOnOutsideClick={true}
        menu={
          <SearchableMenu
            onSelect={props.onAdd}
            optionDatas={props.availableEnzymes
              .filter((enzyme: Enzyme) => !rowIds.includes(enzyme.id))
              .map(({id, name}: {id: number; name: string}) => ({
                value: id,
                name,
              }))}
            width={Menu.WIDTHS.FULL}
          />
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
          size={ButtonSize.SMALL}
          text={
            <div
              {...getTestProps({component: 'codonOptimizationCutsitesPicker', element: 'addCutSiteButton'})}
            >
              <BenchlingIcon icon="create" />
              <span className={css(styles.buttonText)}>{props.buttonText}</span>
            </div>
          }
        />
      </Dropdown>
      <Button
        buttonStyle={ButtonStyle.NEGATIVE}
        className={css(styles.deleteAll)}
        disabled={props.rows.length === 0}
        size={ButtonSize.SMALL}
        text={
          <div>
            <BenchlingIcon icon="delete" />
            <span className={css(styles.buttonText)}>Remove all cut sites</span>
          </div>
        }
        onClick={props.onDeleteAll}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  deleteAll: {
    float: 'right',
  },
  deleteButton: {
    color: GRAY,
    ':hover': {
      color: RED,
    },
    cursor: 'pointer',
  },
  buttonText: {
    marginLeft: 5,
  },
});
